import { useCallback, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router";
import { useTranslation } from "react-i18next";
import {
  Button,
  Input,
  Label,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  toast,
  Separator,
} from "@cliently/ui";
import { LogIn, ShieldCheck, UserPlus, ArrowLeft, Loader2 } from "lucide-react";
import {
  GoogleSignInButton,
  isGoogleOAuthEnabled,
  PasswordStrengthHints,
  TermsAcceptanceCheckbox,
  authApi,
  extractAuthError,
  useAuthV2Store,
} from "@cliently/auth-client";

const API_BASE = import.meta.env.VITE_API_URL
  ? `${(import.meta.env.VITE_API_URL as string).replace(/\/+$/, "")}/api`
  : "/api";

const apiPost = async <T = unknown,>(path: string, body: unknown): Promise<T> => {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? "Erreur serveur");
  return data as T;
};

type ValidateResult =
  | {
      status: "no_account";
      clientId: string;
      firstName: string;
      lastName: string;
    }
  | { status: "has_account" }
  | { status: "new_client"; orgName: string };

/**
 * Page d'auth du portail client (auth-v2). Deux onglets :
 *  - "signin"   : login email/password + Google.
 *  - "first-time" : flux historique d'activation (accessCode ou slug)
 *                   qui aboutit à `authApi.signup` puis link-account côté API.
 */
export const LoginPageV2 = () => {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const slug = params.get("slug") ?? undefined;
  const emailFromUrl = params.get("email") ?? undefined;
  const [tab, setTab] = useState<"signin" | "first-time">(
    slug ? "first-time" : "signin",
  );

  return (
    <div className="bg-muted/30 flex min-h-screen items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="bg-primary/10 mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full">
            <ShieldCheck className="text-primary h-7 w-7" />
          </div>
          <CardTitle className="text-2xl">{t("portal.title")}</CardTitle>
          <CardDescription>{t("portal.desc")}</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs
            value={tab}
            onValueChange={(v) => {
              if (v === "signin" || v === "first-time") setTab(v);
            }}
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signin">
                <LogIn className="mr-2 h-4 w-4" />
                {t("portal.tabSignIn")}
              </TabsTrigger>
              <TabsTrigger value="first-time">
                <UserPlus className="mr-2 h-4 w-4" />
                {t("portal.tabFirstTime")}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="signin">
              <SignInFormV2 />
            </TabsContent>
            <TabsContent value="first-time">
              <FirstTimeFormV2
                onSwitchToSignIn={() => setTab("signin")}
                slug={slug}
                initialEmail={emailFromUrl}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

const SignInFormV2 = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const setSession = useAuthV2Store((s) => s.setSession);

  const finishAuth = useCallback(
    (result: Awaited<ReturnType<typeof authApi.login>>) => {
      if (result.kind === "totp_required") {
        navigate(
          `/auth/2fa?challenge=${encodeURIComponent(result.mfaChallenge)}&redirect=/dashboard`,
        );
        return;
      }
      setSession({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        accessExpiresIn: result.accessExpiresIn,
        identity: result.identity,
      });
      navigate("/dashboard", { replace: true });
    },
    [navigate, setSession],
  );

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await authApi.login({ email: email.trim(), password });
      finishAuth(result);
    } catch (err) {
      toast.error(extractAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const onGoogleCredential = useCallback(
    async (idToken: string) => {
      if (!acceptedTerms) return;
      setLoading(true);
      try {
        const result = await authApi.loginGoogle({
          idToken,
          audience: "client",
          acceptedTerms: true,
        });
        finishAuth(result);
      } catch (err) {
        toast.error(extractAuthError(err));
      } finally {
        setLoading(false);
      }
    },
    [acceptedTerms, finishAuth],
  );

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="signin-email">{t("portal.email")}</Label>
        <Input
          id="signin-email"
          type="email"
          autoComplete="email"
          placeholder={t("portal.emailPlaceholder")}
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoFocus
        />
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="signin-password">{t("portal.password")}</Label>
          <Link
            to="/auth/reset-password"
            className="text-primary text-xs hover:underline"
          >
            {t("portal.forgotPassword")}
          </Link>
        </div>
        <Input
          id="signin-password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>
      <Button
        type="submit"
        className="w-full"
        disabled={loading || !email.trim() || !password}
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <>
            <LogIn className="mr-2 h-4 w-4" />
            {t("portal.signInSubmit")}
          </>
        )}
      </Button>
      {isGoogleOAuthEnabled() && (
        <>
          <div className="relative my-2">
            <Separator />
            <span className="bg-card text-muted-foreground absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 px-3 text-xs">
              {t("portal.or")}
            </span>
          </div>
          <TermsAcceptanceCheckbox
            id="signin-terms"
            checked={acceptedTerms}
            onCheckedChange={setAcceptedTerms}
            disabled={loading}
          />
          <GoogleSignInButton
            onCredential={onGoogleCredential}
            label="signin_with"
            disabled={!acceptedTerms || loading}
          />
        </>
      )}
    </form>
  );
};

const FirstTimeFormV2 = ({
  onSwitchToSignIn,
  slug,
  initialEmail,
}: {
  onSwitchToSignIn: () => void;
  slug?: string;
  initialEmail?: string;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState<"validate" | "signup" | "verify-email">(
    "validate",
  );
  const [accessCode, setAccessCode] = useState("");
  const [email, setEmail] = useState(initialEmail?.trim() ?? "");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isNewClient, setIsNewClient] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleValidate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = slug
        ? await apiPost<ValidateResult>("/portal/validate-access-by-slug", {
            slug: slug.trim(),
            email: email.trim(),
          })
        : await apiPost<ValidateResult>("/portal/validate-access", {
            accessCode: accessCode.trim(),
            email: email.trim(),
          });

      if (result.status === "has_account") {
        toast.info(t("portal.alreadyHasAccount"));
        setTimeout(onSwitchToSignIn, 2000);
        return;
      }

      if (result.status === "new_client") {
        setIsNewClient(true);
        setFirstName("");
        setLastName("");
      } else {
        setIsNewClient(false);
        setFirstName(result.firstName);
        setLastName(result.lastName);
      }
      setStep("signup");
    } catch (err: unknown) {
      toast.error(
        err instanceof Error ? err.message : t("portal.connectionError"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const signup = await authApi.signup({
        email: email.trim(),
        password,
        audience: "client",
        acceptedTerms: true,
      });
      try {
        if (slug) {
          await apiPost("/portal/link-account-by-slug", {
            slug: slug.trim(),
            email: email.trim(),
            authIdentityId: signup.authIdentityId,
            firstName: firstName.trim(),
            lastName: lastName.trim(),
          });
        } else {
          await apiPost("/portal/link-account", {
            accessCode: accessCode.trim(),
            email: email.trim(),
            authIdentityId: signup.authIdentityId,
            firstName: firstName.trim(),
            lastName: lastName.trim(),
          });
        }
      } catch (linkErr) {
        console.error("link-account failed:", linkErr);
        toast.error(
          linkErr instanceof Error
            ? linkErr.message
            : t("portal.connectionError"),
        );
        return;
      }
      setStep("verify-email");
    } catch (err) {
      toast.error(extractAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  if (step === "verify-email") {
    return (
      <div className="space-y-4 text-center">
        <p className="text-muted-foreground text-sm">
          {t("portal.verificationSent", { email })}
        </p>
        <p className="text-muted-foreground text-xs">
          Cliquez sur le lien reçu pour activer votre compte, puis revenez vous
          connecter.
        </p>
        <Button onClick={onSwitchToSignIn} variant="outline" className="w-full">
          <ArrowLeft className="mr-2 h-3 w-3" />
          {t("portal.backToSignIn")}
        </Button>
      </div>
    );
  }

  if (step === "signup") {
    return (
      <form onSubmit={handleSignUp} className="space-y-4">
        <Button
          type="button"
          variant="ghost"
          className="text-muted-foreground hover:text-foreground h-auto gap-1 p-0 text-sm font-normal shadow-none"
          onClick={() => setStep("validate")}
        >
          <ArrowLeft className="h-3 w-3" />
          {t("portal.back")}
        </Button>

        {!isNewClient && (
          <p className="text-muted-foreground text-sm">
            {t("portal.welcomeClient", {
              name: `${firstName} ${lastName}`.trim(),
            })}
          </p>
        )}
        {isNewClient && (
          <p className="text-muted-foreground text-sm">
            {t("portal.newClientWelcome")}
          </p>
        )}
        {isNewClient && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="signup-first">{t("portal.firstName")}</Label>
              <Input
                id="signup-first"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="signup-last">{t("portal.lastName")}</Label>
              <Input
                id="signup-last"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </div>
          </div>
        )}
        <div className="space-y-2">
          <Label>{t("portal.email")}</Label>
          <Input value={email} disabled className="bg-muted" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="signup-password">{t("portal.choosePassword")}</Label>
          <Input
            id="signup-password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoFocus={!isNewClient}
          />
          <PasswordStrengthHints password={password} />
        </div>
        <TermsAcceptanceCheckbox
          id="signup-terms"
          checked={acceptedTerms}
          onCheckedChange={setAcceptedTerms}
          disabled={loading}
        />
        <Button
          type="submit"
          className="w-full"
          disabled={
            loading ||
            !password ||
            !acceptedTerms ||
            (isNewClient && (!firstName.trim() || !lastName.trim()))
          }
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <UserPlus className="mr-2 h-4 w-4" />
              {t("portal.createAccount")}
            </>
          )}
        </Button>
      </form>
    );
  }

  return (
    <form onSubmit={handleValidate} className="space-y-4">
      {!slug && (
        <div className="space-y-2">
          <Label htmlFor="access-code">{t("portal.accessCode")}</Label>
          <Input
            id="access-code"
            placeholder={t("portal.placeholder")}
            value={accessCode}
            onChange={(e) => setAccessCode(e.target.value)}
            className="font-mono tracking-widest"
            autoFocus
            required
          />
        </div>
      )}
      <div className="space-y-2">
        <Label htmlFor="first-email">{t("portal.email")}</Label>
        <Input
          id="first-email"
          type="email"
          autoComplete="email"
          placeholder={t("portal.emailPlaceholder")}
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoFocus={!!slug}
        />
      </div>
      <Button
        type="submit"
        className="w-full"
        disabled={loading || !email.trim() || (!slug && !accessCode.trim())}
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("portal.verify")}
      </Button>
    </form>
  );
};
